import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import Button from '../../common/Button/Button';
import authService from '../../../api/services/authService';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

const OTPVerification = ({ onBackToLogin, onVerifyOTP, loading: externalLoading = false, userEmail, otpData, userCredentials }) => {
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60); // Start with 60 seconds
  const [error, setError] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Start timer when component mounts (OTP was already sent)
  useEffect(() => {
    setResendCooldown(60); // Start 60 second timer
  }, []);

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (userCredentials?.inputType === 'phone') {
        let phoneNumber = userCredentials.emailOrPhone;
        if (!phoneNumber.startsWith('+')) phoneNumber = '+91' + phoneNumber;
        return authService.loginMobileOTP(phoneNumber);
      }
      return authService.loginWithEmailOTP(userCredentials.emailOrPhone);
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'OTP resent successfully!');
        setOtp(''); // Clear current OTP input
        setResendCooldown(60); // Reset timer to 60 seconds
        setError('');
      } else {
        toast.error(result.error || 'Failed to resend OTP');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resend OTP. Please try again.');
    }
  });

  const handleResendOTP = () => {
    if (resendCooldown === 0 && !resendMutation.isPending) {
      resendMutation.mutate();
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 1) {
      const newOtp = otp.split('');
      newOtp[index] = cleanValue;
      const combinedOtp = newOtp.join('');
      setOtp(combinedOtp);
      setError('');

      // Auto-focus next input
      if (cleanValue && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (otp.length === 6) {
        onVerifyOTP(otp);
      } else {
        setError('Please enter a 6-digit code');
      }
      return;
    }

    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Prevent non-numeric characters
    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue relative overflow-hidden">
        <div className="absolute top-10 right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>

        <div className="flex flex-col justify-center px-12 text-white relative z-10">
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="h-16 w-auto object-contain mr-4" />
              <div className="flex flex-col text-white">
                <span className="text-xl font-bold tracking-tight">{process.env.REACT_APP_COMPANY_NAME}</span>
              </div>
            </div>
            <p className="text-sm opacity-80">{process.env.REACT_APP_COMPANY_TAGLINE}</p>
          </div>

          <div className="mb-8">
            <h1 className="text-6xl font-bold mb-4">OTP</h1>
            <p className="text-xl opacity-90">Verification Required</p>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
            <p className="text-sm mb-2">A security code has been sent to your registered device.</p>
            <p className="text-sm">Please enter it to continue.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - OTP Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <div className="w-32 h-32 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
            </div>
          </div>

          <div className="mb-8">
            <button
              onClick={onBackToLogin}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Code</h2>
            <p className="text-gray-600">Enter code sent to {userEmail || 'you'}</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="flex space-x-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={otp[index] || ''}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
                      if (pastedData.length > 0) {
                        setOtp(pastedData);
                        setError('');
                        const nextIndex = Math.min(pastedData.length, 5);
                        const nextInput = document.getElementById(`otp-${nextIndex}`);
                        if (nextInput) nextInput.focus();
                      }
                    }}
                    className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="0"
                  />
                ))}
              </div>
            </div>

            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              loading={externalLoading}
              onClick={() => {
                if (otp.length === 6) {
                  onVerifyOTP(otp);
                } else {
                  setError('Please enter a 6-digit code');
                }
              }}
              disabled={otp.length !== 6 && !error}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Verify
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={resendCooldown > 0 || resendMutation.isPending}
                className={`text-sm font-medium transition-colors ${resendCooldown > 0 || resendMutation.isPending
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-agrosaf-green hover:text-agrosaf-darkGreen'
                  }`}
              >
                {resendMutation.isPending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
              </button>
            </div>

            {otpData && otpData.otp && process.env.NODE_ENV === 'development' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Development Mode</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>OTP Code: <span className="font-mono font-bold text-lg">{otpData.otp}</span></p>
                      <p className="mt-1">This OTP is valid for {otpData.expiresIn || '5 minutes'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
